import {Routes, Route, BrowserRouter, Navigate, matchPath, useLocation} from "react-router";
import {PatientVisitPage} from "../../protocols/studies/PatientVisitPage";
import {ProtocolDefinitionEditor} from "../../protocols/planning/ProtocolDefinitionEditor";
import React, {useEffect} from "react";
import {PageHeader} from "../../common/components/PageHeader";
import classes from "./RouterPage.module.css";
import {ProtocolRecorder} from "../../protocols/studies/ProtocolRecorder";
import {ProtocolListPage} from "../../protocols/studies/review/ProtocolListPage";
import {ProtocolReviewPage} from "../../protocols/studies/review/ProtocolReviewPage";
import {Dashboard} from "../../protocols/analysis/dashboards/Dashboard";
import {PDefDashboard} from "../../protocols/analysis/dashboards/pdef/PDefDashboard";
import {ProtocolDashboard} from "../../protocols/analysis/dashboards/protocol/ProtocolDashboard";
import {LoggedOutPage} from "./LoggedOutPage";
import {UserSettingsPage} from "./UserSettingsPage";
import {useNeurotuneStore} from "../../common/store/neurotune";
import {useTauriListeners} from "../../common/components/TauriListeners";
import {PlanningPage} from "@/protocols/planning/PlanningPage.tsx";
import cx from "clsx";

// this is required to setup dayjs with the correct plugins and locale

/**
 * Represents the properties for a router page component, extending from DashComponentProps.
 * It provides configurations for state persistence and routing behavior.
 */
export interface RouterPageProps {
    /**
     * Represents the url base name
     */
    basename?: string
}

const selectableRoutes = [
    {
        path: "/planning",
        name: "Planning"
    },
    {
        path: "/sessions",
        name: "Sessions"
    },
    {
        path: "/sessions/review",
        name: "Review"
    },
    {
        default: true,
        path: "/analysis",
        name: "Dashboard"
    },
]

/**
 * A React functional component responsible for handling routing and rendering the primary structure of the application.
 * It sets up a `BrowserRouter` with a specified basename, application routes, and manages data about active companies
 * and subjects through `useNeurotuneStore` state hooks.
 *
 * This component initialises required data by loading available companies and setting the default active company.
 * Once the active company is set, associated subjects are loaded. Routes are dynamically rendered based on the
 * application's routing configuration.
 *
 * If the required data (e.g., active company and subjects) has not been loaded, a loading overlay is displayed.
 *
 * Props:
 * @prop {string} [basename="/d/"] - The base URL for the React Router, used for navigating within the application.
 * @prop {Function} setProps - A callback function for setting additional properties or configurations from outside the component.
 *
 * State Management:
 * - `subjects`: List of subjects loaded via `useNeurotuneStore`.
 * - `loadSubjects`: Function to load subjects for the active company.
 * - `activeCompany`: The currently active company selected via `useNeurotuneStore`.
 * - `setActiveCompany`: Function to set the active company.
 * - `loadCompanies`: Function to load the list of available companies.
 *
 * Effects:
 * - `useEffect` to load companies on mount, set the first company as active, and handle errors.
 * - `useEffect` to load associated subjects when the active company is updated.
 *
 * Returns:
 * - If the required data (active company and subjects) is available, the component renders the router with routes and content.
 * - If the required data is not yet available, a loading overlay is displayed.
 */
export const Layout: React.FC = () => {
    const location = useLocation();
    const isProtocolRecorder = matchPath("/sessions/visit/:subject_id/:protocol_id", location.pathname);

    return (
        <div className={cx(!isProtocolRecorder ? classes.root : classes.rootNoHeader)}>
            {/*style={{backgroundImage: "url('/d/assets/images/nerves.jpg'), url('/images/nerves.jpg')"}}>*/}
            {!isProtocolRecorder && (
                <div className={classes.header} aria-label="Navigation header">
                    <PageHeader routes={selectableRoutes}/>
                </div>)
            }
            <div className={classes.content} role="main">
                <Routes>
                    <Route path={"/planning/:section"} element={<PlanningPage/>}/>
                    <Route path={"/planning"} element={<PlanningPage/>}/>
                    <Route path={"/planning/pdef/:pdef_id"} element={<ProtocolDefinitionEditor/>}/>
                    <Route path={"/sessions/visit"} element={<PatientVisitPage/>}/>
                    <Route path={"/sessions/visit/:subject_id/:protocol_id"} element={<ProtocolRecorder/>}/>
                    <Route path={"/sessions/review"} element={<ProtocolListPage/>}/>
                    <Route path={"/sessions/review/:protocolId"} element={<ProtocolReviewPage/>}/>
                    <Route path={"/sessions"} element={<PatientVisitPage/>}/>
                    <Route path={"/analysis"} element={<Dashboard/>}/>
                    <Route path={"/analysis/dashboards/pdef/:pdef_id"} element={<PDefDashboard/>}/>
                    <Route path={"/analysis/dashboards/protocol/:protocol_id"} element={<ProtocolDashboard/>}/>
                    <Route path={"/settings/:section?"} element={<UserSettingsPage/>}/>
                    <Route path={"/logged_out"} element={<LoggedOutPage/>}/>
                    {/*fallback route */}
                    <Route path="*" element={<Navigate to="/analysis"/>}/>
                </Routes>
            </div>
        </div>
    );
};

export const RouterPage: React.FC<RouterPageProps> = ({basename = "/"}) => {
    const whoAmI = useNeurotuneStore(state => state.whoAmI)

    // Initialize Tauri listeners after company is set (ensures correct companyId in events)
    useTauriListeners();

    useEffect(() => {
        // Refresh the user state
        whoAmI().catch(() => {
            // ignore errors
        })
    },[])

    return (
        <BrowserRouter basename={basename}>
            <Layout />
        </BrowserRouter>
    )

}